import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

function initSimpleCartDiscountToggle() {
  if (window.__stSimpleCartDiscountToggleInitialized) return;
  window.__stSimpleCartDiscountToggleInitialized = true;

  document.addEventListener(
    'click',
    (event) => {
      const toggle = event.target instanceof Element
        ? event.target.closest('[data-st-discount-toggle]')
        : null;

      if (!(toggle instanceof HTMLElement)) return;

      const wrapper = toggle.closest('[data-st-discount-simple]');
      if (!(wrapper instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const content = wrapper.querySelector('[data-st-discount-content]');
      if (!(content instanceof HTMLElement)) return;

      const isOpen = toggle.getAttribute('aria-expanded') === 'true';

      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');

      if (isOpen) {
        content.hidden = true;
        content.setAttribute('hidden', '');
      } else {
        content.hidden = false;
        content.removeAttribute('hidden');

        const input = content.querySelector('.cart-discount__input');

        window.requestAnimationFrame(() => {
          if (input instanceof HTMLInputElement) {
            input.focus({ preventScroll: true });
          }
        });
      }

      const dialog = wrapper.closest('dialog');

      if (dialog instanceof HTMLDialogElement && !dialog.open) {
        try {
          dialog.showModal();
        } catch (error) {
          dialog.setAttribute('open', '');
        }
      }

      document.body.classList.add('overflow-hidden');
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) return;

      const insideSimpleDiscount = target.closest('[data-st-discount-simple]');

      if (!insideSimpleDiscount) return;

      event.stopPropagation();
    },
    true
  );

  document.addEventListener(
    'mousedown',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) return;

      const insideSimpleDiscount = target.closest('[data-st-discount-simple]');

      if (!insideSimpleDiscount) return;

      event.stopPropagation();
    },
    true
  );
}

initSimpleCartDiscountToggle();

/**
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError
 * @property {HTMLElement} cartDiscountErrorDiscountCode
 * @property {HTMLElement} cartDiscountErrorShipping
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback?.();
    initSimpleCartDiscountToggle();

    this.addEventListener('click', this.#stopInnerBubble);
    this.addEventListener('pointerdown', this.#stopInnerBubble);
    this.addEventListener('mousedown', this.#stopInnerBubble);
    this.addEventListener('keydown', this.#stopInnerBubble);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.removeEventListener('click', this.#stopInnerBubble);
    this.removeEventListener('pointerdown', this.#stopInnerBubble);
    this.removeEventListener('mousedown', this.#stopInnerBubble);
    this.removeEventListener('keydown', this.#stopInnerBubble);
  }

  #stopInnerBubble = (event) => {
    event.stopPropagation();
  };

  #keepDrawerOpen() {
    const dialog = this.closest('dialog');

    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      try {
        dialog.showModal();
      } catch (error) {
        dialog.setAttribute('open', '');
      }
    }

    document.body.classList.add('overflow-hidden');
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles applying a discount code.
   * @param {SubmitEvent} event
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();

      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        this.#keepDrawerOpen();
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];

      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);

        if (
          codes.length === existingDiscounts.length &&
          codes.every((code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          this.#keepDrawerOpen();
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);

      this.#keepDrawerOpen();
    } catch (error) {
      // Ignore aborted requests
    } finally {
      this.#activeFetch = null;
      this.#keepDrawerOpen();
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);

    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);

      this.#keepDrawerOpen();
    } catch (error) {
      // Ignore aborted requests
    } finally {
      this.#activeFetch = null;
      this.#keepDrawerOpen();
    }
  };

  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  #existingDiscounts() {
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');

    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}